/**
 * "Awaiting reply" (spec §14): Sent messages aged 3–90 days that carry a Message-ID and
 * recipients and are referenced by no other cached message (`inReplyTo` / `references`).
 * Computed from the newest 1500 cached rows.
 */
import type { MessageRow } from '$lib/types';

export const FOLLOWUP_SCAN_ROWS = 1500;
export const FOLLOWUP_MIN_DAYS = 3;
export const FOLLOWUP_MAX_DAYS = 90;

export interface Followup {
  msg: MessageRow;
  ageDays: number;
}

const norm = (s: string | null | undefined) => (s || '').trim().toLowerCase();
const normId = (s: string | null | undefined) => norm(String(s || '')).replace(/^<|>$/g, '');
const timeOf = (r: MessageRow) => Date.parse(r.date) || Date.parse(r.receivedAt || '') || 0;

function collectRefs(rows: readonly MessageRow[]): Set<string> {
  const ids = new Set<string>();
  for (const r of rows) {
    const p = normId(r.inReplyTo);
    if (p) ids.add(p);
    for (const ref of r.references ?? []) {
      const n = normId(ref);
      if (n) ids.add(n);
    }
  }
  return ids;
}

/**
 * @param sent rows of the Sent folder
 * @param others every other cached row (replies live in INBOX, Archive, …)
 * @param now epoch ms
 */
export function findFollowups(
  sent: readonly MessageRow[],
  others: readonly MessageRow[],
  now: number,
  minDays = FOLLOWUP_MIN_DAYS,
  maxDays = FOLLOWUP_MAX_DAYS,
): Followup[] {
  const repliedFromOthers = collectRefs(others);
  const repliedFromSent = collectRefs(sent);
  const out: Followup[] = [];
  for (const m of sent) {
    const id = normId(m.messageId);
    if (!id) continue;
    const at = timeOf(m);
    if (!at) continue;
    const age = (now - at) / 864e5;
    if (age < minDays || age > maxDays) continue;
    if (repliedFromOthers.has(id) || repliedFromSent.has(id)) continue;
    if (!(m.toAddrs ?? []).map(norm).filter(Boolean).length) continue;
    out.push({ msg: m, ageDays: Math.floor(age) });
  }
  return out.sort((a, b) => b.ageDays - a.ageDays);
}
